import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getFixedAssetDetails } from '../api/dms/fixedAsset.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { AttachmentAction, AttachmentAssetType, IAttachment, IAttachmentContainer } from '../models/dms/attachment.model';
import { setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';
import { RootState } from '.';
import { IFixedAssetDetails } from '../models/dms/fixedAsset.model';

interface IFixedAssetState {
  selectedFixedAsset?: IFixedAssetDetails;
  selectedFixedAssetLoading: boolean;
}

const initialState: IFixedAssetState = {
  selectedFixedAsset: undefined,
  selectedFixedAssetLoading: false,
};

/// THUNKS

export const setSelectedFixedAssetStoreAsync = createAsyncThunk<{}, IFixedAssetDetails>('fixedAsset/setSelectedFixedAssetStoreAsync', async (fixedAsset, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected fixed asset '${fixedAsset.unitDescription}' to storage`, '[fixedAsset] setSelectedFixedAssetStoreAsync');
  try {
    dispatch(setSelectedFixedAssetState(fixedAsset));
    await updateConfigObject<IFixedAssetDetails>(ConfigName.SelectedFixedAsset, fixedAsset, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchFixedAssetDetailsStoreAsync = createAsyncThunk<{}, { unitId: string; repairOrderNumber: number }>('fixedAsset/fetchFixedAssetDetailsStoreAsync', async ({ unitId, repairOrderNumber }, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching fixed asset details from database by unit id '${unitId}'`, '[fixedAsset] fetchFixedAssetDetailsStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setSelectedFixedAssetLoadingState(true));

    let attachmentContainer = {
      assetType: AttachmentAssetType.FixedAsset,
      key: unitId,
      jobCode: '',
      jobType: '',
      repairOrderNumber: repairOrderNumber.toString(),
      mediaRef: '',
      attachments: [] as IAttachment[],
    } as IAttachmentContainer;

    await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

    const responseData = await getFixedAssetDetails(unitId);
    await dispatch(setSelectedFixedAssetStoreAsync(responseData));

    // Be sure to include existing unsync attachments
    const { selectedVehicleAttachments } = (getState() as RootState).vehicleAttachments;
    const outstandingAttachments = selectedVehicleAttachments?.attachments.filter((a) => a.action === AttachmentAction.Add) || [];

    // Set the vehicle attachments
    attachmentContainer = {
      ...attachmentContainer,
      attachments: [...responseData.attachments.map((a) => ({ ...a, assetType: AttachmentAssetType.FixedAsset })), ...outstandingAttachments] as IAttachment[],
    } as IAttachmentContainer;

    await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

    dispatch(setIsLoadingCountState(-1));
    dispatch(setSelectedFixedAssetLoadingState(false));

    return responseData;
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSelectedFixedAssetLoadingState(false));
  }
});

/// SLICE

export const fixedAssetSlice = createSlice({
  name: 'fixedAsset',
  initialState,
  reducers: {
    setSelectedFixedAssetState: (state, action: PayloadAction<IFixedAssetDetails>) => {
      logConsole(LogType.Info, '[fixedAsset] setSelectedFixedAssetState');
      state.selectedFixedAsset = action.payload;
    },
    setSelectedFixedAssetLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[fixedAsset] setSelectedFixedAssetLoadingState: ${action.payload}`);
      state.selectedFixedAssetLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedFixedAssetState, setSelectedFixedAssetLoadingState } = fixedAssetSlice.actions;

/// CUSTOM SELECTORS

export default fixedAssetSlice.reducer;
